/**
 * frontend/src/pages/valuation/_ValueBlocked.tsx
 *
 * VAL-FIX-PAY-BUGS-1 (P1, bug 2) — what "Your value" and the pay screen show when the
 * value cannot be worked out yet: one plain sentence and a link to the step that fixes
 * it. Never the server's text, a status or a code (an owner is a client). The step is
 * the server's answer (`details.fix_step` on a refusal, `blocker.fix_step` on the pay
 * screen); the label and route come from the one step list (_steps.ts).
 *
 * CLIENT_TEXT is PROPOSED wording, pending owner approval (VAL-FIX-PAY-BUGS-1 report).
 */
import { Link } from 'react-router-dom'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import type { ValueBlocker } from '@/api/valuationPayment'
import { formatDateCentral } from '@/utils/date'
import { stepsFor } from './_steps'

export const CLIENT_TEXT = {
  'blocked.questions': 'Some required questions are not answered yet. Answer them on Questions to see your value.',
  'blocked.ownerPay': 'Enter what it would cost to pay someone else to run the business, on Adjustments and rates, to see your value.',
  'blocked.step': 'Your value cannot be worked out yet. Finish {step} to see your value.',
  'blocked.go': 'Go to {step}',
  'blocked.retry': 'Your value could not be worked out just now. Please try again.',
  'blocked.retryButton': 'Try again',
  'paid.on': 'Paid on {date}',
} as const

/** The owner step a blocker points at (label + route), or null when it names none. */
function fixStep(blocker: ValueBlocker, engagementId: string) {
  const step = stepsFor('valuation_owner').find(s => s.key === blocker.fix_step)
  return step ? { label: step.label, to: step.route(engagementId) } : null
}

export function blockedSentence(blocker: ValueBlocker, label: string | null): string {
  if (blocker.fix_reason === 'unanswered_questions') return CLIENT_TEXT['blocked.questions']
  if (blocker.fix_reason === 'replacement_compensation') return CLIENT_TEXT['blocked.ownerPay']
  return label ? CLIENT_TEXT['blocked.step'].replace('{step}', label) : CLIENT_TEXT['blocked.retry']
}

export function PaidOn({ paidAt }: { paidAt: string | null | undefined }) {
  const day = formatDateCentral(paidAt ?? null)  // VAL-68 B4: the Central day
  if (!day) return null
  return <p data-testid="paid-on" className="text-xs text-pal-ink-muted">{CLIENT_TEXT['paid.on'].replace('{date}', day)}</p>
}

export function ValueBlocked({ engagementId, blocker, paidAt, onRetry }: {
  engagementId: string
  /** null = the refusal named no step: plain "try again" words and a button. */
  blocker: ValueBlocker | null
  paidAt?: string | null
  onRetry?: () => void
}) {
  const step = blocker ? fixStep(blocker, engagementId) : null
  return (
    <Card data-testid="value-blocked">
      <CardContent className="space-y-3 p-6 text-sm text-pal-ink-body">
        <PaidOn paidAt={paidAt} />
        <p>{blocker ? blockedSentence(blocker, step?.label ?? null) : CLIENT_TEXT['blocked.retry']}</p>
        {step ? (
          <Link to={step.to} className={buttonVariants({ size: 'sm' })}>
            {CLIENT_TEXT['blocked.go'].replace('{step}', step.label)}
          </Link>
        ) : onRetry ? (
          <Button size="sm" variant="outline" onClick={onRetry}>{CLIENT_TEXT['blocked.retryButton']}</Button>
        ) : null}
      </CardContent>
    </Card>
  )
}
